import { useContext, useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ClockIcon,
  AcademicCapIcon,
  CheckBadgeIcon,
  ListBulletIcon,
  ComputerDesktopIcon,
  ArrowsPointingOutIcon,
  ArrowTopRightOnSquareIcon,
  NoSymbolIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import {
  getExamDetail,
  getMyAttempts,
  startAttempt,
} from "../../services/Attempts/AttemptService";
import SimpleLoader from "../../components/Loader/SimpleLoader";
import { Button } from "../../components/forms/Buttons/Button";
import { isCanceledError, resolveExamErrorText } from "../../utils/examError";
import { getEntityLabel } from "../../utils/entityLabel";
import LanguageDropdown from "../../components/LanguageDropdown/LanguageDropdown";
import { UserContext } from "../../utils/contexts/UserContext";
import { canOpenPage } from "../../utils/permissions";
import { examStatus, examStatusLabel } from "../Exams/ExamStatus";
import { BackLink, StudentTitle } from "./parts";

// The exam may not be started here: the API hides an exam that is not active from students (404 on its page) and
// refuses to start it (400 {exam_uuid: ["Exam not found or inactive."]}).
const isUnavailable = (error) =>
  error?.response?.status === 404 ||
  (error?.response?.status === 400 && Array.isArray(error.response.data?.exam_uuid));

// The user's attempt of the exam that still runs, or null: a student no longer sees an exam taken from the active
// ones, but an attempt they began goes on until its time is up.
const findRunningAttempt = (uuid, signal) =>
  getMyAttempts({ params: { exam: uuid, status: "IN_PROGRESS", page_size: 1 }, signal })
    .then((data) => {
      const attempt = (Array.isArray(data) ? data : data?.results || [])[0];
      return attempt && (!attempt.expires_at || attempt.remaining_seconds > 0) ? attempt : null;
    })
    .catch(() => null);

export default function ExamDetail() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useContext(UserContext) || {};

  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  // The exam is not (or no longer) open to the user: said on the page instead of a raw error.
  const [unavailable, setUnavailable] = useState(false);
  // Its attempt the user began, still running: continued from here.
  const [runningAttempt, setRunningAttempt] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let isCurrent = true;

    getExamDetail({ uuid, signal: controller.signal })
      .then((data) => {
        if (!isCurrent) return;
        setExam(data);
        setLoading(false);
      })
      .catch(async (error) => {
        if (!isCurrent || isCanceledError(error)) {
          return;
        }
        if (isUnavailable(error)) {
          // Looked up before the page says anything: a running attempt is what the student came back for.
          const attempt = await findRunningAttempt(uuid, controller.signal);
          if (!isCurrent) return;
          setRunningAttempt(attempt);
          setUnavailable(true);
          setLoading(false);
          return;
        }
        toast.error(
          resolveExamErrorText(
            t,
            error,
            "exam_taking.failed_to_load_exam_details"
          )
        );
        setLoading(false);
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [uuid, t]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const attempt = await startAttempt({
        data: { exam_uuid: uuid },
      });
      navigate(`/exam/attempt/${attempt.uuid}`);
    } catch (error) {
      if (isUnavailable(error)) {
        // Taken from the active exams since the page opened: nothing to retry.
        setUnavailable(true);
        return;
      }
      toast.error(
        resolveExamErrorText(t, error, "exam_taking.failed_to_start_exam")
      );
    } finally {
      setStarting(false);
    }
  };

  if (loading) return <SimpleLoader className="h-8 w-8" />;
  if (unavailable) {
    const examName = exam?.name || runningAttempt?.exam?.name;
    return (
      <div className="mx-auto w-full max-w-3xl motion-safe:animate-fade-in">
        <BackLink to="/available-exams" className="mb-4">
          {t("exam_taking.back_to_exams")}
        </BackLink>
        <div role="alert" className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
          {runningAttempt ? (
            <>
              <span className="grid h-12 w-12 place-items-center rounded-full bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-600/20">
                <ClockIcon className="h-6 w-6" aria-hidden="true" />
              </span>
              <h1 className="text-lg font-semibold tracking-tight text-slate-900">{t("exam_detail.running_title")}</h1>
              {examName && <p className="max-w-full break-words text-sm font-medium text-slate-700">{examName}</p>}
              <p className="max-w-md text-sm text-slate-600">{t("exam_detail.running_text")}</p>
              <Button
                text={t("exam_taking.continue_exam")}
                color="primary"
                onClick={() => navigate(`/exam/attempt/${runningAttempt.uuid}`)}
                className="mt-2 max-sm:h-11"
              />
            </>
          ) : (
            <>
              <span className="grid h-12 w-12 place-items-center rounded-full bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-500/20">
                <LockClosedIcon className="h-6 w-6" aria-hidden="true" />
              </span>
              <h1 className="text-lg font-semibold tracking-tight text-slate-900">{t("exam_detail.unavailable_title")}</h1>
              {examName && <p className="max-w-full break-words text-sm font-medium text-slate-700">{examName}</p>}
              <p className="max-w-md text-sm text-slate-600">{t("exam_detail.unavailable")}</p>
              {canOpenPage(user, "/my-attempts") && (
                <>
                  <p className="max-w-md text-sm text-slate-500">{t("exam_detail.attempts_hint")}</p>
                  <Link
                    to="/my-attempts"
                    className="mt-1 inline-flex h-10 items-center justify-center rounded-lg bg-white px-5 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 max-sm:h-11"
                  >
                    {t("nav.my_attempts")}
                  </Link>
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
  if (!exam) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <BackLink to="/available-exams" className="mb-4">
          {t("exam_taking.back_to_exams")}
        </BackLink>
        <p className="py-2 text-sm text-slate-500">{t("exam_taking.exam_not_found")}</p>
      </div>
    );
  }

  // A general exam asks every question of its tests; a subject exam (DOMAIN_SUBJECT) only those of the student's
  // subject, so it is told in tests.
  const questionCount =
    exam.exam_type === "GENERAL" && Array.isArray(exam.exam_tests)
      ? exam.exam_tests.reduce((sum, examTest) => sum + (examTest.test?.questions_count || 0), 0)
      : null;
  // Most exams have no category: then there is no card with a dash for it.
  const category = exam.category_name || getEntityLabel(exam.category, "");
  const facts = [
    { key: "duration", icon: ClockIcon, label: t("exam_taking.duration"), value: t("exam_taking.minutes_value", { minutes: exam.duration_minutes }) },
    {
      key: "questions",
      icon: ListBulletIcon,
      label: questionCount === null ? t("exam_detail.tests") : t("exam_taking.total_questions"),
      value:
        questionCount === null
          ? t("exam_taking.tests_count", { count: exam.tests_count ?? 0 })
          : t("question_list.count", { count: questionCount }),
    },
    { key: "passing", icon: CheckBadgeIcon, label: t("exam_taking.passing_score"), value: `${exam.passing_score}%` },
    category && { key: "category", icon: AcademicCapIcon, label: t("exam_taking.category"), value: category },
  ].filter(Boolean);

  return (
    <div className="mx-auto w-full max-w-3xl motion-safe:animate-fade-in">
      <div className="mb-4 flex items-center justify-between gap-3">
        <BackLink to="/available-exams">{t("exam_taking.back_to_exams")}</BackLink>
        {/* The exam's language is chosen before it starts. From md up the sidebar shows the same picker. */}
        <LanguageDropdown className="md:hidden" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
        <StudentTitle className="break-words" rowClassName={exam.description ? "mb-2" : "mb-5"}>
          {exam.name}
        </StudentTitle>
        {exam.description && (
          <p className="mb-6 leading-7 text-slate-600">{exam.description}</p>
        )}

        {/* Only exam managers open an exam that is not active: students do not see it. */}
        {examStatus(exam) && examStatus(exam) !== "active" && (
          <p className="mb-6 flex items-start gap-2 rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-900 ring-1 ring-inset ring-blue-600/10">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
            {t("exam_detail.not_active_note", { status: examStatusLabel(t, examStatus(exam)) })}
          </p>
        )}

        {/* Two columns from 360px, so the rules and the start button come up on phones; three facts share one row
            only from lg, where the card is wide enough beside the sidebar. In two columns an odd last fact takes the
            whole row. */}
        <div className={`mb-6 grid grid-cols-1 gap-3 min-[360px]:grid-cols-2 ${facts.length === 3 ? "lg:grid-cols-3" : ""}`}>
          {facts.map(({ key, ...fact }) => (
            <InfoCard key={key} {...fact} className="min-[360px]:odd:last:col-span-2 lg:odd:last:col-span-1" />
          ))}
        </div>

        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/70 p-4 sm:p-5">
          <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-800">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {t("exam_taking.exam_rules")}
          </h2>
          <ul className="flex flex-col gap-2.5">
            {[
              { icon: ArrowsPointingOutIcon, key: "rule_fullscreen" },
              { icon: ArrowTopRightOnSquareIcon, key: "rule_tab" },
              { icon: ComputerDesktopIcon, key: "rule_split_screen" },
              { icon: NoSymbolIcon, key: "rule_copy" },
              { icon: ClockIcon, key: "rule_timer" },
              { icon: ExclamationTriangleIcon, key: "rule_leave" },
            ].map(({ icon: Icon, key }) => (
              <li key={key} className="flex items-start gap-2.5 text-sm leading-6 text-amber-900">
                <Icon className="mt-1 h-4 w-4 flex-shrink-0 text-amber-600" />
                <span>{t(`exam_taking.${key}`)}</span>
              </li>
            ))}
          </ul>
          <label className="mt-4 flex cursor-pointer select-none items-center gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2.5 transition duration-200 hover:border-amber-300">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-4 focus:ring-blue-600/15 focus:ring-offset-0"
            />
            <span className="text-sm font-medium text-slate-900">
              {t("exam_taking.rules_acknowledge")}
            </span>
          </label>
        </div>

        <Button
          text={t("exam_taking.start_exam")}
          color="primary"
          loading={starting}
          onClick={handleStart}
          disabled={!acknowledged}
          className="!h-12 w-full !rounded-xl text-base"
        />
      </div>
    </div>
  );
}

// One fact about the exam (duration, questions, passing score, category). Phones, two facts side by side, leave
// the icon out so the value keeps its room; a value or label too long for its card wraps instead of being cut.
function InfoCard({ icon: Icon, label, value, className = "" }) {
  return (
    <div className={`flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3 ${className}`}>
      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 max-sm:hidden">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="break-words text-xs text-slate-500">{label}</p>
        <p className="break-words font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  );
}
